import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CandidateNomination, Election, ElectoralDistrict, NominationStatus, Page, Party } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const DECISION_ROLES = ['REGISTRATION_OFFICER', 'ELECTORAL_COMMISSIONER'];
const STATUSES: NominationStatus[] = ['SUBMITTED', 'ELIGIBILITY_CHECK', 'DEPOSIT_PENDING', 'ACCEPTED', 'REJECTED', 'WITHDRAWN'];

/** GET/POST /api/v1/m39/nominations — officer console (decisionMaker) + self/officer filing. */
export function Nominations() {
  const { persona } = useAuth();
  const isDecisionMaker = persona?.roles.some((r) => DECISION_ROLES.includes(r)) ?? false;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialElectionId = params.get('electionId') ?? '';
  const [status, setStatus] = useState<NominationStatus | ''>('');
  const [q, setQ] = useState('');
  const [electionId] = useState(initialElectionId);
  const [showCreate, setShowCreate] = useState(false);

  const qp = new URLSearchParams({ limit: '50' });
  if (status) qp.set('status', status);
  if (q.trim()) qp.set('q', q.trim());
  if (electionId) qp.set('electionId', electionId);
  const listQ = useApiQuery<Page<CandidateNomination>>(isDecisionMaker ? `/api/v1/m39/nominations?${qp.toString()}` : null);
  const mineQ = useApiQuery<{ items: CandidateNomination[] }>(!isDecisionMaker && persona?.nin ? '/api/v1/m39/nominations/me' : null);

  return (
    <div>
      <PageHeader
        title="Candidate Nominations"
        subtitle="E-filing, M01/M05-backed eligibility checks, and the nomination deposit."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + File nomination
          </button>
        }
      />

      {isDecisionMaker ? (
        <>
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <Field label="Candidate NIN or name contains" className="min-w-[220px] flex-1">
              <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} />
            </Field>
            <Field label="Status" className="!mb-0">
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as NominationStatus | '')}>
                <option value="">All</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
            </Field>
            {electionId ? <span className="text-xs text-slate-400">Filtered to election {electionId}</span> : null}
          </div>
          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No nominations match" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Candidate</th>
                    <th className="table-head">Seat</th>
                    <th className="table-head">Status</th>
                    <th className="table-head">Deposit</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((n) => (
                    <tr key={n.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/elections/nominations/${n.id}`)}>
                      <td className="table-cell font-medium">
                        {n.candidateName} <span className="font-mono text-xs text-slate-400">({n.candidateNin})</span>
                      </td>
                      <td className="table-cell text-xs">{n.seatLabel}</td>
                      <td className="table-cell">
                        <StatusBadge status={n.status} />
                      </td>
                      <td className="table-cell text-xs">
                        <StatusBadge status={n.depositStatus} /> <span className="text-slate-400">{scr(n.depositAmountScr)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <>
          <RoleNotice roles={DECISION_ROLES} />
          <h2 className="mb-2 text-sm font-bold text-slate-700">My nominations</h2>
          {!persona?.nin ? (
            <EmptyState title="No verified NIN on this token" hint="A self-filed nomination needs a verified NIN." />
          ) : mineQ.loading ? (
            <Spinner />
          ) : mineQ.error ? (
            <ErrorNote message={mineQ.error} />
          ) : (mineQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="You have not filed a nomination" />
          ) : (
            <div className="card overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Seat</th>
                    <th className="table-head">Status</th>
                    <th className="table-head">Deposit</th>
                  </tr>
                </thead>
                <tbody>
                  {mineQ.data!.items.map((n) => (
                    <tr key={n.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/elections/nominations/${n.id}`)}>
                      <td className="table-cell font-medium">{n.seatLabel}</td>
                      <td className="table-cell">
                        <StatusBadge status={n.status} />
                      </td>
                      <td className="table-cell text-xs">
                        <StatusBadge status={n.depositStatus} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showCreate ? (
        <FileNominationModal
          isDecisionMaker={isDecisionMaker}
          onClose={() => setShowCreate(false)}
          onDone={(id) => {
            setShowCreate(false);
            navigate(`/elections/nominations/${id}`);
          }}
        />
      ) : null}
    </div>
  );
}

function FileNominationModal({ isDecisionMaker, onClose, onDone }: { isDecisionMaker: boolean; onClose: () => void; onDone: (id: string) => void }) {
  const electionsQ = useApiQuery<Page<Election>>('/api/v1/m39/elections?status=NOMINATIONS_OPEN&limit=50');
  const districtsQ = useApiQuery<Page<ElectoralDistrict>>('/api/v1/m39/districts?limit=50');
  const partiesQ = useApiQuery<Page<Party>>('/api/v1/m39/parties?status=ACTIVE&limit=50');
  const [electionId, setElectionId] = useState('');
  const [electoralDistrictId, setElectoralDistrictId] = useState('');
  const [candidateNin, setCandidateNin] = useState('');
  const [candidateName, setCandidateName] = useState('');
  const [affiliation, setAffiliation] = useState<'independent' | 'party'>('independent');
  const [partyId, setPartyId] = useState('');
  const [declaration, setDeclaration] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const selectedElection = electionsQ.data?.items.find((e) => e.id === electionId);
  const needsDistrict = selectedElection?.type === 'NATIONAL_ASSEMBLY';

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ nomination: { id: string } }>('/api/v1/m39/nominations', {
        electionId,
        electoralDistrictId: needsDistrict ? electoralDistrictId : undefined,
        candidateNin: candidateNin || undefined,
        candidateName,
        partyId: affiliation === 'party' ? partyId : undefined,
        independent: affiliation === 'independent',
        papers: { declaration: declaration || 'Nomination papers filed via One Gov Governance portal.' },
      });
      onDone(res.nomination.id);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to file nomination');
    } finally {
      setBusy(false);
    }
  };

  const valid =
    electionId.length > 0 &&
    candidateName.trim().length >= 2 &&
    (!needsDistrict || electoralDistrictId.length > 0) &&
    (affiliation === 'independent' || partyId.length > 0);

  return (
    <Modal title="File a candidate nomination" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Election (NOMINATIONS_OPEN only)">
          <select className="field-input" value={electionId} onChange={(e) => setElectionId(e.target.value)}>
            <option value="">Select…</option>
            {(electionsQ.data?.items ?? []).map((el) => (
              <option key={el.id} value={el.id}>
                {el.title} ({el.type.replaceAll('_', ' ')})
              </option>
            ))}
          </select>
        </Field>
        {needsDistrict ? (
          <Field label="Electoral district (constituency)">
            <select className="field-input" value={electoralDistrictId} onChange={(e) => setElectoralDistrictId(e.target.value)}>
              <option value="">Select…</option>
              {(districtsQ.data?.items ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Candidate name">
            <input className="field-input" value={candidateName} onChange={(e) => setCandidateName(e.target.value)} />
          </Field>
          <Field label={isDecisionMaker ? 'Candidate NIN (officer-assisted; blank = your own)' : 'Candidate NIN (blank = your own verified NIN)'}>
            <input className="field-input" value={candidateNin} onChange={(e) => setCandidateNin(e.target.value)} />
          </Field>
        </div>
        <Field label="Affiliation">
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={affiliation === 'independent'} onChange={() => setAffiliation('independent')} /> Independent
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" checked={affiliation === 'party'} onChange={() => setAffiliation('party')} /> Party-affiliated
            </label>
          </div>
        </Field>
        {affiliation === 'party' ? (
          <Field label="Party">
            <select className="field-input" value={partyId} onChange={(e) => setPartyId(e.target.value)}>
              <option value="">Select…</option>
              {(partiesQ.data?.items ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <Field label="Declaration (nomination papers content)">
          <textarea className="field-input" rows={3} value={declaration} onChange={(e) => setDeclaration(e.target.value)} placeholder="Statement of candidacy…" />
        </Field>
        <p className="text-xs text-slate-500">
          Filing runs live CITIZENSHIP/MINIMUM_AGE/NOT_DECEASED (M01) and CRIMINAL_DISQUALIFICATION (M05) checks immediately — you&rsquo;ll land on the
          nomination&rsquo;s detail page to see the outcome.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !valid}>
            {busy ? 'Filing…' : 'File nomination'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
